import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Stack, Typography, IconButton, Box, CircularProgress, alpha, useTheme
} from '@mui/material';
import {
  AssignmentLateOutlined,
  Close,
  AutoAwesome,
  CheckCircleOutline,
  WarningAmberOutlined,
} from '@mui/icons-material';

const CreateAssessmentTypeModal = ({ open, handleClose, onSave, isLoading }) => {
  const theme = useTheme();
  const [hasAcknowledged, setHasAcknowledged] = useState(false);

  const { register, handleSubmit, reset, watch, formState: { errors } } = useForm({
    defaultValues: {
      name: '',
      description: ''
    }
  });

  const descriptionValue = watch('description');

  useEffect(() => {
    if (!open) {
      setHasAcknowledged(false);
    }
  }, [open]);

  const onSubmit = (data) => {
    onSave({
      name: data.name.trim(),
      description: data.description.trim(),
    });
    reset();
    setHasAcknowledged(false);
  };

  const handleModalClose = () => {
    reset();
    setHasAcknowledged(false);
    handleClose();
  };

  const descriptionLength = (descriptionValue || '').trim().length;
  const isDescriptionStrong = descriptionLength >= 40;

  return (
    <Dialog
      open={open}
      onClose={handleModalClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 3,
          p: 1,
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.12)',
        }
      }}
    >
      <DialogTitle sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        pb: 1,
        pt: 2,
        px: 3
      }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box sx={{
            bgcolor: alpha('#2469F0', 0.08),
            p: 1,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <AssignmentLateOutlined sx={{ color: '#2469F0' }} />
          </Box>
          <Typography variant="h6" fontWeight={700} color="#0F172A">
            New Assessment Type
          </Typography>
        </Stack>
        <IconButton
          onClick={handleModalClose}
          size="small"
          sx={{
            bgcolor: '#F1F5F9',
            '&:hover': { bgcolor: '#E2E8F0' }
          }}
        >
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pt: 1 }}>
        {!hasAcknowledged ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              py: 3,
              px: 1,
            }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                bgcolor: alpha('#B54708', 0.1),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2,
              }}
            >
              <WarningAmberOutlined sx={{ fontSize: 32, color: '#B54708' }} />
            </Box>

            <Typography
              variant="h6"
              fontWeight={700}
              color="#0F172A"
              sx={{ mb: 1, fontSize: '1rem' }}
            >
              Please read before you begin
            </Typography>

            <Typography
              variant="body2"
              color="#475467"
              sx={{ mb: 2.5, fontSize: '0.85rem', lineHeight: 1.6, maxWidth: 400 }}
            >
              The <b>description you provide will be sent directly to the AI model</b> as
              context. It tells the AI <b>what this assessment measures, what to look for,
              and how to score risk</b>.
            </Typography>

            <Box
              sx={{
                width: '100%',
                p: 2,
                borderRadius: 2,
                bgcolor: alpha('#2469F0', 0.04),
                border: `1px dashed ${alpha('#2469F0', 0.25)}`,
                textAlign: 'left',
                mb: 3,
              }}
            >
              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1 }}>
                <AutoAwesome sx={{ fontSize: 16, color: '#2469F0' }} />
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 700, color: '#1A5BD4', fontSize: '0.75rem' }}
                >
                  Your description must answer
                </Typography>
              </Stack>
              <Stack spacing={0.75}>
                {[
                  'What does this assessment measure?',
                  'What signs or symptoms should be flagged?',
                  'How should risk levels be interpreted?',
                ].map((item, i) => (
                  <Stack
                    key={i}
                    direction="row"
                    spacing={1}
                    alignItems="flex-start"
                  >
                    <CheckCircleOutline
                      sx={{ fontSize: 14, color: '#027A48', mt: '2px', flexShrink: 0 }}
                    />
                    <Typography
                      variant="caption"
                      sx={{ color: '#475467', fontSize: '0.75rem', lineHeight: 1.5 }}
                    >
                      {item}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Box>

            <Typography
              variant="caption"
              sx={{
                color: '#B54708',
                fontSize: '0.75rem',
                fontWeight: 600,
                mb: 2.5,
              }}
            >
              Vague descriptions produce inaccurate AI results.
            </Typography>
          </Box>
        ) : (
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            <Box>
              <Typography
                variant="caption"
                fontWeight={600}
                color="#0F172A"
                sx={{ mb: 1, display: 'block' }}
              >
                ASSESSMENT NAME *
              </Typography>
              <TextField
                fullWidth
                placeholder="e.g. Nutrition & Hydration"
                {...register('name', {
                  required: 'Name is required',
                  minLength: { value: 3, message: 'Name is too short' }
                })}
                error={!!errors.name}
                helperText={errors.name?.message}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: '#F8FAFC',
                    '&:hover': { bgcolor: '#F1F5F9' },
                    '&.Mui-focused': { bgcolor: '#FFFFFF' }
                  }
                }}
              />
            </Box>

            <Box>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <Typography variant="caption" fontWeight={600} color="#0F172A">
                  AI CONTEXT / DESCRIPTION *
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    fontSize: '0.7rem',
                    color: isDescriptionStrong ? '#027A48' : '#B54708'
                  }}
                >
                  {descriptionLength} chars {isDescriptionStrong && '✓'}
                </Typography>
              </Stack>

              <TextField
                fullWidth
                multiline
                rows={6}
                placeholder="Example: This assessment evaluates the client's nutritional intake and hydration status. Flag concerns if weight loss exceeds 5% in 30 days, fluid intake drops below 1.5L/day, or signs of dehydration (dry mouth, dark urine, confusion) are observed. Score risk as HIGH if 2+ concerns present."
                {...register('description', {
                  required: 'Description is required — this is what the AI reads',
                  minLength: {
                    value: 40,
                    message: 'Please write at least 40 characters so the AI understands the context'
                  }
                })}
                error={!!errors.description}
                helperText={
                  errors.description?.message ||
                  'Sent verbatim to the AI model as context.'
                }
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: '#F8FAFC',
                    '&:hover': { bgcolor: '#F1F5F9' },
                    '&.Mui-focused': { bgcolor: '#FFFFFF' }
                  },
                  '& .MuiFormHelperText-root': {
                    fontSize: '0.7rem',
                    fontWeight: 500,
                  }
                }}
              />
            </Box>
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{
        p: 3,
        pt: 2,
        gap: 1.5,
        borderTop: `1px solid ${alpha('#E2E8F0', 0.6)}`
      }}>
        {!hasAcknowledged ? (
          <>
            <Button
              onClick={handleModalClose}
              variant="outlined"
              sx={{
                color: '#64748B',
                borderColor: '#E2E8F0',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: 2,
                px: 3,
                '&:hover': {
                  borderColor: '#94A3B8',
                  bgcolor: '#F8FAFC'
                }
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => setHasAcknowledged(true)}
              variant="contained"
              startIcon={<CheckCircleOutline />}
              sx={{
                bgcolor: '#2469F0',
                borderRadius: 2,
                px: 4,
                textTransform: 'none',
                fontWeight: 600,
                boxShadow: `0 2px 8px ${alpha('#2469F0', 0.25)}`,
                '&:hover': {
                  bgcolor: '#1A5BD4',
                  boxShadow: `0 4px 16px ${alpha('#2469F0', 0.35)}`,
                },
                '&:active': { bgcolor: '#1553B8' }
              }}
            >
              Got It, Continue
            </Button>
          </>
        ) : (
          <>
            <Button
              onClick={handleModalClose}
              variant="outlined"
              sx={{
                color: '#64748B',
                borderColor: '#E2E8F0',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: 2,
                px: 3,
                '&:hover': {
                  borderColor: '#94A3B8',
                  bgcolor: '#F8FAFC'
                }
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isLoading}
              onClick={handleSubmit(onSubmit)}
              sx={{
                bgcolor: '#2469F0',
                borderRadius: 2,
                px: 4,
                textTransform: 'none',
                fontWeight: 600,
                boxShadow: `0 2px 8px ${alpha('#2469F0', 0.25)}`,
                '&:hover': {
                  bgcolor: '#1A5BD4',
                  boxShadow: `0 4px 16px ${alpha('#2469F0', 0.35)}`,
                },
                '&:active': { bgcolor: '#1553B8' }
              }}
            >
              {isLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                'Create Category'
              )}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CreateAssessmentTypeModal;